import React, { useEffect, useId, useState } from 'react';
import {
  Grid, FormControl, FormHelperText, InputLabel, Select, MenuItem, TextField, InputAdornment, Box, Button, Typography,
} from '@mui/material';
import { VpnKey as KeyIcon } from '@mui/icons-material';
import registryService from '../services/registryService';
import {
  MSG_NO_KEY, MSG_NOT_KEY, MSG_KEY_SEMANTICS, registryKeyField, registryStatus, registryTargetError,
} from '../utils/registryBindings';
import { MONO_FONT } from '../constants/typography';

export const REGISTRY_FETCH_CAP = 200; // page size
export const REGISTRY_FETCH_MAX_PAGES = 25; // upper bound: 5000 registries

/**
 * Every registry with local entries (internal, or external with a local-entry source): the
 * list endpoint also returns the others, so it is paged through (`last` of the page response) up to REGISTRY_FETCH_MAX_PAGES pages.
 */
export const defaultFetchLocalEntryRegistries = async () => {
  const all = [];
  for (let page = 0; page < REGISTRY_FETCH_MAX_PAGES; page += 1) {
    const data = await registryService.getAll(page, REGISTRY_FETCH_CAP);
    const content = Array.isArray(data?.content) ? data.content : [];
    all.push(...content);
    if (data?.last !== false || content.length === 0) break;
  }
  return all.filter((r) => r.hasLocalEntries === true);
};

/**
 * One registry fetch shared by several pickers: the first call fetches, later calls get
 * the same promise. A failed fetch is forgotten, so the next caller retries it.
 */
export const shareRegistryFetch = (fetchRegistries) => {
  let pending = null;
  return () => {
    if (!pending) {
      pending = Promise.resolve(fetchRegistries()).catch((err) => {
        pending = null;
        throw err;
      });
    }
    return pending;
  };
};

/**
 * Picks a target registry with local entries; the bound field is always the registry's key
 * (`key: true`), filled in automatically. Value is { registryId, field }. Keyless
 * registries cannot be chosen. Once the registries are loaded, a stored value is checked
 * like the backend does: a missing, without local entries or keyless registry is flagged on the
 * registry (the stored id stays visible), a non-key field (legacy) gets a one-click fix
 * whose accessible name is `keyFixLabel`. Label ids are per-instance (useId) so several
 * pickers can share a page.
 */
const RegistryFieldPicker = ({
  value,
  onChange,
  fetchRegistries = defaultFetchLocalEntryRegistries,
  keyFixLabel = 'Usa la chiave',
}) => {
  const uid = useId();
  const registryLabelId = `${uid}-registry`;
  const fieldId = `${uid}-field`;
  const [registries, setRegistries] = useState(null); // null = not loaded (or failed): nothing is flagged
  const [loadError, setLoadError] = useState(false); // last fetch failed: shown with a "Riprova" action
  const [attempt, setAttempt] = useState(0); // bumped by "Riprova" to re-run the fetch
  const registryId = value?.registryId || '';
  const field = value?.field || '';

  useEffect(() => {
    let active = true;
    Promise.resolve(fetchRegistries())
      .then((list) => {
        if (!active) return;
        setRegistries(Array.isArray(list) ? list : []);
        setLoadError(false);
      })
      .catch(() => {
        if (!active) return;
        setRegistries(null);
        setLoadError(true);
      });
    return () => { active = false; };
  }, [fetchRegistries, attempt]);

  const options = registries || [];
  const status = registryStatus(registries, registryId);
  const selected = status.registry;
  const keyField = status.key;
  const missing = Boolean(registryId) && status.state === 'missing';
  const registryError = registryId && (missing || status.state === 'keyless')
    ? registryTargetError(status, field) : null;
  const mismatch = Boolean(selected && keyField && field && field !== keyField);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth size="small" error={Boolean(registryError)}>
          <InputLabel id={registryLabelId}>Anagrafica</InputLabel>
          <Select
            labelId={registryLabelId} label="Anagrafica" value={selected || missing ? registryId : ''}
            onChange={(e) => {
              const next = options.find((r) => r.id === e.target.value);
              onChange({ registryId: e.target.value, field: registryKeyField(next) || '' });
            }}
          >
            {missing && (
              <MenuItem value={registryId} disabled sx={{ fontFamily: MONO_FONT }}>{registryId}</MenuItem>
            )}
            {options.map((r) => {
              const hasKey = Boolean(registryKeyField(r));
              return (
                <MenuItem key={r.id} value={r.id} disabled={!hasKey}>
                  {hasKey ? (r.name || r.code) : `${r.name || r.code} — ${MSG_NO_KEY}`}
                </MenuItem>
              );
            })}
          </Select>
          {registryError && <FormHelperText>{registryError}</FormHelperText>}
          {loadError && (
            <FormHelperText error sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 1 }}>
              Impossibile caricare le anagrafiche
              <Button size="small" onClick={() => setAttempt((a) => a + 1)}>Riprova</Button>
            </FormHelperText>
          )}
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          id={fieldId} label="Campo chiave" size="small" fullWidth
          value={mismatch || registryError ? field : (selected ? (keyField || '') : '')}
          error={mismatch}
          InputProps={{
            readOnly: true,
            sx: { fontFamily: MONO_FONT },
            startAdornment: (
              <InputAdornment position="start">
                <KeyIcon sx={{ fontSize: 16, color: 'primary.main' }} />
              </InputAdornment>
            ),
          }}
          helperText={mismatch ? `Chiave: «${keyField}»` : (registryId ? undefined : 'Scegli un\'anagrafica')}
        />
      </Grid>
      {mismatch && (
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 1 }}>
            <Typography variant="caption" color="error">{MSG_NOT_KEY(keyField)}</Typography>
            <Button size="small" aria-label={keyFixLabel} onClick={() => onChange({ registryId, field: keyField })}>
              Usa la chiave
            </Button>
            <Typography variant="caption" color="text.secondary">{MSG_KEY_SEMANTICS(keyField)}</Typography>
          </Box>
        </Grid>
      )}
    </Grid>
  );
};

export default RegistryFieldPicker;
